import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook that tracks whether a component has been viewed and whether it is currently in view.
 *
 * @param {Object} options - IntersectionObserver options.
 * @param {number} [options.threshold=0.25] - A number between 0 and 1 indicating the percentage of the target's visibility the observer's callback should be executed.
 *
 * @returns {Object} - An object containing:
 *  - `isInView` {boolean} - Whether the component is currently in view.
 *  - `hasBeenViewed` {boolean} - Whether the component has been viewed at least once.
 *  - `elementRef` {React.RefObject} - A ref to be assigned to the element to be observed.
 */
export function useComponentInView(options = { threshold: 0.25 }) {
  const [hasBeenViewed, setHasBeenViewed] = useState<boolean>(false);
  const [isInView, setIsInView] = useState<boolean>(false);
  const elementRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsInView(entry.isIntersecting);
    }, options); // Pass the options to the IntersectionObserver

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [options]);

  useEffect(() => {
    if (isInView && !hasBeenViewed) {
      setHasBeenViewed(true);
    }
  }, [isInView, hasBeenViewed]);

  return { isInView, hasBeenViewed, elementRef };
}
